import { Action, createAsyncAction, createReducer } from 'typesafe-actions';
import { produce } from 'immer';
import type { ReduxError } from '@universal/errors';
import type { GetProductsResponse, MyProfileForm, ProductType, ProfileForm } from '@dtp/membership-service-types';

// Actions
export enum Actions {
  GET_BECOME_A_MEMBER = 'naf/becomeAMember/GET_BECOME_A_MEMBER',
  GET_BECOME_A_MEMBER_SUCCESS = 'naf/becomeAMember/GET_BECOME_A_MEMBER_SUCCESS',
  GET_BECOME_A_MEMBER_FAIL = 'naf/becomeAMember/GET_BECOME_A_MEMBER_FAIL',
  GET_BECOME_A_MEMBER_CANCEL = 'naf/becomeAMember/GET_BECOME_A_MEMBER_CANCEL',
}

export interface State {
  data: {
    products: Record<ProductType['productNumber'], ProductType>;
    myProfileForm?: MyProfileForm;
    householdMemberForm?: ProfileForm;
    campaignCodeDetails?: GetProductsResponse['customerProducts']['campaignCodeDetails'] & {
      hasAvailableCampaignCodes?: boolean;
    };
    // Populated by the SSR data handler (handleBliMedlem); not yet mapped by the client success
    // reducer, so declared optional. See NOA-3244 backlog before relying on it client-side.
    consents?: GetProductsResponse['consents'];
  };
  isUpdating: boolean;
  errorState?: any;
}

export const initialState: State = {
  data: {
    products: {},
    myProfileForm: undefined,
    householdMemberForm: undefined,
    campaignCodeDetails: undefined,
  },
  isUpdating: false,
};

export const actions = {
  getBecomeAMember: createAsyncAction(
    Actions.GET_BECOME_A_MEMBER, // request payload creator
    Actions.GET_BECOME_A_MEMBER_SUCCESS, // success payload creator
    Actions.GET_BECOME_A_MEMBER_FAIL, // failure payload creator
    Actions.GET_BECOME_A_MEMBER_CANCEL, // optional cancel payload creator
  )<string | undefined, GetProductsResponse, ReduxError, undefined>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getBecomeAMember.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = true;
    }),
  )
  .handleAction(actions.getBecomeAMember.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.data.myProfileForm = action.payload.profileForm || undefined;
      draftState.data.householdMemberForm = action.payload.householdMemberForm || undefined;
      draftState.data.products = (action.payload?.customerProducts?.eligibleProducts ?? []).reduce(
        (acc: Record<ProductType['productNumber'], ProductType>, cur) => {
          acc[cur.productNumber] = cur;
          return acc;
        },
        {},
      );
      draftState.data.campaignCodeDetails = action.payload?.customerProducts?.campaignCodeDetails;
    }),
  )
  .handleAction(actions.getBecomeAMember.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.errorState = action.payload;
    }),
  )
  .handleAction(actions.getBecomeAMember.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
    }),
  );
